// react
import { SetURLSearchParams } from 'react-router-dom';

// l18n
import { TFunction } from 'i18next';

// mui
import { Typography } from '@mui/material';

// icons
import { ModeEditOutlined as IconModeEdit } from '@mui/icons-material';

// table
import { TableHeader } from '../../../ui/Table/Table';

// types
import { Document, DocumentType, Permissions, User } from '../../../../@types';

// ui
import { BlockchainHash } from '../../../ui/BlockchainHash/BlockchainHash';
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { MenuItemProps } from '../../../ui/Menu/Menu';
import { Time } from '../../../ui/Time/Time';
import { UserAvatar } from '../../../ui/UserAvatar/UserAvatar';

// links
import { LinkDocument } from '../../../links/LinkDocument/LinkDocument';

// props
export interface DocumentHeaderTableProps {
	hasBtnMenu: boolean;
	searchParams: URLSearchParams;
	setSearchParams: SetURLSearchParams;
	t: TFunction<'translation', undefined>;
	userProjectPermissions?: Permissions | null;
}

export const getDocumentsHeaders = ({
	hasBtnMenu,
	searchParams,
	setSearchParams,
	t,
	userProjectPermissions,
}: DocumentHeaderTableProps): TableHeader[] => {
	const columns: TableHeader[] = [
		{
			label: t('name'),
			value: 'name',
			render: (name: string, header: TableHeader, doc: Document) => {
				return <LinkDocument document={doc} underline="hover" />;
			},
		},
		{
			label: t('type'),
			value: 'type',
			render: (type: DocumentType) => {
				return <Typography>{type ? type.name : t('na')}</Typography>;
			},
		},
		{
			label: t('date'),
			value: 'created',
			render: (created: Date) => {
				return <Time date={new Date(created)} />;
			},
		},
		{
			label: t('hash'),
			value: 'notarizationHash',
			render: (notarizationHash: string) => {
				return notarizationHash ? (
					<BlockchainHash hash={notarizationHash} />
				) : (
					<Typography>{t('na')}</Typography>
				);
			},
		},
		{
			label: t('uploaded'),
			value: 'user',
			render: (user: User) => {
				return <UserAvatar user={user} />;
			},
		},
	];

	if (hasBtnMenu) {
		columns.push({
			align: 'right',
			label: null,
			value: 'id',
			render: (id: number) => {
				const btnMenuItems: MenuItemProps[] = [];

				if (userProjectPermissions?.document?.update) {
					btnMenuItems.push({
						children: t('editDocument'),
						icon: <IconModeEdit />,
						onClick: () => {
							searchParams.set('dialog', 'editDocument');
							searchParams.set('dialogDocument', String(id));
							setSearchParams(searchParams);
						},
					});
				}

				if (btnMenuItems.length > 0) {
					return <BtnMenu items={btnMenuItems} />;
				}

				return null;
			},
		});
	}

	return columns;
};
